import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { donationKeys, queueKeys } from '@/features/donation/queries';
import { api } from '@/lib/api';

type OperationListInput = Parameters<typeof api.donation.operationList>[0];

export const operationKeys = {
  all: ['operations'] as const,
  campaigns: () => ['operations', 'campaigns'] as const,
  list: (input: OperationListInput) => ['operations', 'list', input] as const,
  counts: (input: Record<string, unknown>) => ['operations', 'counts', input] as const,
};

/**
 * The campaigns the reader carries work out for, with what waits in their
 * reach — the menu's badges and the operations pages' header. Read behind
 * `donation.fulfill`, which a staff assignment derives (docs/decisions/0091),
 * so it is asked only by a reader who holds or derives it.
 */
export function useOperationCampaigns(enabled: boolean) {
  return useQuery({
    queryKey: operationKeys.campaigns(),
    queryFn: () => api.donation.operationCampaigns({}),
    enabled,
    staleTime: 60_000,
  });
}

export function useOperationList(input: OperationListInput | null) {
  return useQuery({
    queryKey: operationKeys.list(input ?? ({} as OperationListInput)),
    queryFn: () => api.donation.operationList(input as OperationListInput),
    enabled: input !== null,
    placeholderData: (previous) => previous,
  });
}

/**
 * Every operation moves the page's stages and their counts, the menu's
 * badges, the order and the donation list, so they share one invalidation.
 */
function useOperationMutation<I, R>(run: (input: I) => Promise<R>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: operationKeys.all });
      void queryClient.invalidateQueries({ queryKey: queueKeys.all });
      void queryClient.invalidateQueries({ queryKey: donationKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['institutionLedger'] });
      void queryClient.invalidateQueries({ queryKey: ['campaignPeriods'] });
    },
  });
}

export function useOperationFulfil() {
  return useOperationMutation((input: Parameters<typeof api.donation.operationFulfil>[0]) =>
    api.donation.operationFulfil(input),
  );
}

export function useOperationUnfulfil() {
  return useOperationMutation((input: Parameters<typeof api.donation.operationUnfulfil>[0]) =>
    api.donation.operationUnfulfil(input),
  );
}

export function useOperationQueue() {
  return useOperationMutation((input: Parameters<typeof api.donation.bulkQueue>[0]) =>
    api.donation.bulkQueue(input),
  );
}

export function useOperationDequeue() {
  return useOperationMutation((input: Parameters<typeof api.donation.bulkDequeue>[0]) =>
    api.donation.bulkDequeue(input),
  );
}
